// MPEG-TS muxer for the guest uplink: one H.264 program with an optional
// AAC/ADTS stream, one TS blob per encoded frame (participant-loop-contracts
// §2.2). Grown from the rift demo muxer at pin 066ca29
// (demos/rift-webcodecs-demo/tsmuxer.js), which was video-only.
//
// Timing. Every timestamp handed in is a WebCodecs chunk timestamp in µs, and
// the uplink takes both tracks' timestamps from the same capture clock
// (MediaStreamTrackProcessor frames), so audio and video PTS share one
// timeline. The first timestamp seen, less PTS_ORIGIN_MARGIN_US, becomes PTS
// zero, so an audio frame captured slightly before the first video frame
// still gets a positive PTS.
//
// PCR. Carried on the video PID in the first packet of every video PES, from
// the same capture clock as the PTS (PCR = DTS − PCR_LEAD). Because PCR and
// PTS are derived from one clock there is no PCR/PTS drift to compensate for
// however long the session runs; the capture clock's drift against the
// gateway's wallclock is the gateway's to absorb — it re-anchors browser PTS
// to arrival wallclock for ISO recording (§5), and JBS decodes on arrival.
// The 27 MHz extension is always 0: 90 kHz resolution is all a PTS-locked
// PCR can carry. The demo's "no drift compensation" caveat does not apply to
// a multi-hour session for that reason; what does apply is the 33-bit wrap
// (~26.5 h), handled by reducing every value mod 2^33.
import { crc32Mpeg2 } from './crc32.js';
import { PID_AUDIO, PID_PAT, PID_PMT, PID_VIDEO, PROGRAM_NUMBER, PTS_WRAP, STREAM_ID_AUDIO, STREAM_ID_VIDEO, STREAM_TYPE_AAC_ADTS, STREAM_TYPE_H264, SYNC_BYTE, TRANSPORT_STREAM_ID, TS_PACKET_LEN, } from './constants.js';
export const PTS_ORIGIN_MARGIN_US = 1_000_000;
/** PCR runs this far (90 kHz ticks) behind the DTS it travels with. */
export const PCR_LEAD_90K = 9_000;
function writeTimestamp(buf, off, prefix, ts) {
    // 5 bytes: pppp t[32..30] 1 | t[29..22] | t[21..15] 1 | t[14..7] | t[6..0] 1
    const hi = Math.floor(ts / 2 ** 30) & 0x07;
    const lo = ts % 2 ** 30;
    buf[off] = (prefix << 4) | (hi << 1) | 0x01;
    buf[off + 1] = (lo >>> 22) & 0xff;
    buf[off + 2] = (((lo >>> 15) & 0x7f) << 1) | 0x01;
    buf[off + 3] = (lo >>> 7) & 0xff;
    buf[off + 4] = ((lo & 0x7f) << 1) | 0x01;
}
function concat(parts) {
    let total = 0;
    for (const p of parts)
        total += p.length;
    const out = new Uint8Array(total);
    let off = 0;
    for (const p of parts) {
        out.set(p, off);
        off += p.length;
    }
    return out;
}
export class TsMuxer {
    audio;
    cc = new Map();
    originUs = null;
    constructor(opts) {
        this.audio = opts.audio;
    }
    /** 90 kHz ticks, mod 2^33, for a WebCodecs µs timestamp. */
    to90k(us) {
        if (this.originUs === null)
            this.originUs = us - PTS_ORIGIN_MARGIN_US;
        const ticks = Math.round(((us - this.originUs) * 9) / 100);
        return ((ticks % PTS_WRAP) + PTS_WRAP) % PTS_WRAP;
    }
    nextCC(pid) {
        const c = this.cc.get(pid) ?? 0;
        this.cc.set(pid, (c + 1) & 0x0f);
        return c;
    }
    psiPacket(pid, section) {
        const pkt = new Uint8Array(TS_PACKET_LEN).fill(0xff);
        pkt[0] = SYNC_BYTE;
        pkt[1] = 0x40 | ((pid >> 8) & 0x1f);
        pkt[2] = pid & 0xff;
        pkt[3] = 0x10 | this.nextCC(pid);
        pkt[4] = 0x00; // pointer_field
        pkt.set(section, 5);
        return pkt;
    }
    withCrc(section) {
        const body = Uint8Array.from(section);
        const crc = crc32Mpeg2(body);
        const out = new Uint8Array(body.length + 4);
        out.set(body, 0);
        out[body.length] = (crc >>> 24) & 0xff;
        out[body.length + 1] = (crc >>> 16) & 0xff;
        out[body.length + 2] = (crc >>> 8) & 0xff;
        out[body.length + 3] = crc & 0xff;
        return out;
    }
    buildPat() {
        // section_length = 5 (header after length) + 4 (one program) + 4 (CRC)
        return this.psiPacket(PID_PAT, this.withCrc([
            0x00,
            0xb0,
            13,
            (TRANSPORT_STREAM_ID >> 8) & 0xff,
            TRANSPORT_STREAM_ID & 0xff,
            0xc1, // version 0, current_next 1
            0x00,
            0x00,
            (PROGRAM_NUMBER >> 8) & 0xff,
            PROGRAM_NUMBER & 0xff,
            0xe0 | ((PID_PMT >> 8) & 0x1f),
            PID_PMT & 0xff,
        ]));
    }
    buildPmt() {
        const streams = [
            STREAM_TYPE_H264,
            0xe0 | ((PID_VIDEO >> 8) & 0x1f),
            PID_VIDEO & 0xff,
            0xf0,
            0x00,
        ];
        if (this.audio) {
            streams.push(STREAM_TYPE_AAC_ADTS, 0xe0 | ((PID_AUDIO >> 8) & 0x1f), PID_AUDIO & 0xff, 0xf0, 0x00);
        }
        // section_length = 9 (program_number .. program_info_length) + streams + 4 (CRC)
        const sectionLength = 9 + streams.length + 4;
        return this.psiPacket(PID_PMT, this.withCrc([
            0x02,
            0xb0 | ((sectionLength >> 8) & 0x0f),
            sectionLength & 0xff,
            (PROGRAM_NUMBER >> 8) & 0xff,
            PROGRAM_NUMBER & 0xff,
            0xc1,
            0x00,
            0x00,
            0xe0 | ((PID_VIDEO >> 8) & 0x1f), // PCR_PID = video
            PID_VIDEO & 0xff,
            0xf0,
            0x00, // program_info_length = 0
            ...streams,
        ]));
    }
    buildPes(streamId, es, pts90k, bounded) {
        const headerDataLen = 5; // PTS only: no B-frames in realtime Baseline, DTS == PTS
        const pes = new Uint8Array(9 + headerDataLen + es.length);
        pes[0] = 0x00;
        pes[1] = 0x00;
        pes[2] = 0x01;
        pes[3] = streamId;
        // PES_packet_length counts bytes after the length field. Video uses 0
        // (unbounded, allowed for video ES in TS); audio is bounded so a
        // demuxer can emit it without waiting for the next PES.
        const pesLen = bounded ? 3 + headerDataLen + es.length : 0;
        if (pesLen > 0xffff)
            throw new Error(`TS: PES too large for a bounded length (${pesLen})`);
        pes[4] = (pesLen >> 8) & 0xff;
        pes[5] = pesLen & 0xff;
        pes[6] = 0x84; // marker '10', data_alignment_indicator = 1
        pes[7] = 0x80; // PTS only
        pes[8] = headerDataLen;
        writeTimestamp(pes, 9, 0b0010, pts90k);
        pes.set(es, 9 + headerDataLen);
        return pes;
    }
    /**
     * Slice a PES into 188-byte packets. `pcr90k` (video only) puts a PCR in
     * the first packet's adaptation field; `randomAccess` sets its
     * random_access_indicator. The last packet is padded with AF stuffing.
     */
    packetize(pid, pes, pcr90k, randomAccess) {
        const packets = [];
        let off = 0;
        let first = true;
        while (off < pes.length) {
            const pkt = new Uint8Array(TS_PACKET_LEN);
            pkt[0] = SYNC_BYTE;
            pkt[1] = (first ? 0x40 : 0x00) | ((pid >> 8) & 0x1f);
            pkt[2] = pid & 0xff;
            const wantPcr = first && pcr90k !== null;
            const wantFlags = wantPcr || (first && randomAccess);
            // Adaptation field bytes excluding stuffing: length byte + flags byte + PCR.
            let afLen = wantFlags ? 2 + (wantPcr ? 6 : 0) : 0;
            const remaining = pes.length - off;
            let payloadLen = Math.min(TS_PACKET_LEN - 4 - afLen, remaining);
            let stuffing = TS_PACKET_LEN - 4 - afLen - payloadLen;
            if (stuffing > 0 && afLen === 0) {
                // Need an AF just to pad. A 1-byte AF (length 0) pads by one byte;
                // anything more needs the flags byte too.
                afLen = stuffing === 1 ? 1 : 2;
                payloadLen = Math.min(TS_PACKET_LEN - 4 - afLen, remaining);
                stuffing = TS_PACKET_LEN - 4 - afLen - payloadLen;
            }
            pkt[3] = (afLen > 0 ? 0x30 : 0x10) | this.nextCC(pid);
            let w = 4;
            if (afLen > 0) {
                pkt[w++] = afLen - 1 + stuffing; // adaptation_field_length
                if (afLen >= 2) {
                    pkt[w++] = (first && randomAccess ? 0x40 : 0x00) | (wantPcr ? 0x10 : 0x00);
                    if (wantPcr) {
                        const base = pcr90k;
                        const hi = Math.floor(base / 2 ** 25) & 0xff; // base[32..25]
                        const lo = base % 2 ** 25;
                        pkt[w++] = hi;
                        pkt[w++] = (lo >>> 17) & 0xff;
                        pkt[w++] = (lo >>> 9) & 0xff;
                        pkt[w++] = (lo >>> 1) & 0xff;
                        pkt[w++] = ((lo & 0x01) << 7) | 0x7e; // reserved '111111', ext[8] = 0
                        pkt[w++] = 0x00; // ext[7..0] = 0
                    }
                }
                pkt.fill(0xff, w, w + stuffing);
                w += stuffing;
            }
            pkt.set(pes.subarray(off, off + payloadLen), w);
            packets.push(pkt);
            off += payloadLen;
            first = false;
        }
        return packets;
    }
    /**
     * One H.264 access unit (Annex-B, SPS/PPS inline on keyframes) → TS bytes.
     * PAT + PMT precede every keyframe so each GOP decodes on its own — the
     * gateway's ISO segmenter cuts at IDRs and relies on this (§5).
     */
    muxVideo(annexb, timestampUs, keyframe) {
        const pts = this.to90k(timestampUs);
        const pcr = (pts - PCR_LEAD_90K + PTS_WRAP) % PTS_WRAP;
        const parts = [];
        if (keyframe)
            parts.push(this.buildPat(), this.buildPmt());
        parts.push(...this.packetize(PID_VIDEO, this.buildPes(STREAM_ID_VIDEO, annexb, pts, false), pcr, keyframe));
        return concat(parts);
    }
    /** One or more ADTS frames (header included) → TS bytes on the audio PID. */
    muxAudio(adts, timestampUs) {
        if (!this.audio)
            throw new Error('TS: muxer was created without an audio stream');
        const pts = this.to90k(timestampUs);
        return concat(this.packetize(PID_AUDIO, this.buildPes(STREAM_ID_AUDIO, adts, pts, true), null, false));
    }
}
//# sourceMappingURL=muxer.js.map